import { canAccessInnerCircle, canAccessOuterCircle } from '@whispers/sdk';
import { useSearchParams } from 'react-router-dom';

import { useCreateCheckout, useCreatePortal, useProducts } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';

const tiers = ['outer_circle', 'inner_circle'] as const;
type MembershipTier = (typeof tiers)[number];

const getTier = (lookupKey?: string): MembershipTier | undefined => {
  if (lookupKey?.startsWith('inner_circle_')) return 'inner_circle';
  if (lookupKey?.startsWith('outer_circle_')) return 'outer_circle';
  return undefined;
};

const hasTierAccess = (tier: MembershipTier, user: ReturnType<typeof useAuth>['user']): boolean => {
  if (tier === 'inner_circle') return canAccessInnerCircle(user);
  return canAccessOuterCircle(user);
};

export const MembershipPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useProducts({ include: ['prices'] });
  const createCheckout = useCreateCheckout();
  const createPortal = useCreatePortal();
  const requestedTier = getTier(`${searchParams.get('tier') ?? ''}_monthly`);
  const returnTo = searchParams.get('target') ?? '/feeds';

  const openPortal = () => {
    createPortal.mutate(undefined, {
      onSuccess: ({ url }) => {
        window.location.href = url;
      },
    });
  };

  return (
    <>
      <div>
        <h1 className="text-xl font-medium text-text">The Stone Circle</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Choose the membership that suits your path.
        </p>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="grid gap-4">
          {tiers.map((tier) => {
            const prices = (data?.prices ?? []).filter(
              (price) => getTier(price.lookupKey) === tier,
            );
            const product = (data?.products ?? []).find(
              (candidate) => candidate.stripeProductId === prices[0]?.stripeProductId,
            );
            const isCurrent = hasTierAccess(tier, user);
            return (
              <Card key={tier}>
                <h2 className="text-lg font-medium text-text">
                  {product?.name ?? tier.replace('_', ' ')}
                </h2>
                {product?.description && (
                  <p className="mt-2 text-sm text-text-secondary">{product.description}</p>
                )}
                {requestedTier === tier && !isCurrent && (
                  <p className="mt-3 text-sm font-medium text-text">
                    This membership unlocks this space.
                  </p>
                )}
                {isCurrent ? (
                  <p className="mt-4 text-sm text-text-secondary">Included with your membership.</p>
                ) : user && user.subscriptionType !== 'FREE' ? (
                  <Button className="mt-4" onClick={openPortal} disabled={createPortal.isPending}>
                    Manage upgrade in Stripe
                  </Button>
                ) : !user ? (
                  <Button href="/sign-in" className="mt-4">
                    Sign in to join
                  </Button>
                ) : prices.length > 0 ? (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {prices.map((price) => (
                      <Button
                        key={price.id}
                        onClick={() =>
                          createCheckout.mutate(
                            { priceId: price.id, returnTo },
                            {
                              onSuccess: ({ url }) => {
                                window.location.href = url;
                              },
                            },
                          )
                        }
                        disabled={createCheckout.isPending}
                      >
                        Join {price.lookupKey?.endsWith('_yearly') ? 'yearly' : 'monthly'}:{' '}
                        {new Intl.NumberFormat('en-AU', {
                          style: 'currency',
                          currency: price.currency.toUpperCase(),
                        }).format((price.amount ?? 0) / 100)}
                        /{price.recurringInterval ?? 'month'}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-text-secondary">
                    Membership options are coming soon.
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
};
